import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import VerifiedIcon from '@mui/icons-material/Verified';
import FactoryIcon from '@mui/icons-material/Factory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SectionTitle from './shared/SectionTitle';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    backgroundColor: theme.palette.background.paper,
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    borderRadius: theme.spacing(1.5),
    border: '1px solid transparent',
    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
        borderColor: theme.palette.orange?.main || '#e67e22',
    },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
    width: 64,
    height: 64,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
    '& svg': {
        fontSize: '2rem',
    },
}));

const trustPillars = [
    {
        icon: SchoolIcon,
        title: "30+ Years Experience",
        description: "Respected sail designer with extensive knowledge of production cruisers and club racers."
    },
    {
        icon: VerifiedIcon,
        title: "Top-Tier Materials",
        description: "Challenge, Dimension Polyant, and Contender sailcloth. Quality hardware from industry leaders."
    },
    {
        icon: FactoryIcon,
        title: "World-Class Construction",
        description: "Professional loft using top-tier cloth and hardware. We demand the highest quality standards for every sail."
    },
    {
        icon: AttachMoneyIcon,
        title: "Fair, Transparent Pricing",
        description: "High technology, low overhead, no big marketing budgets. Just quality sails at honest prices."
    }
];

const TrustSection: React.FC = () => {
    return (
        <Box>
            <SectionTitle centered sx={{ mb: 5 }}>
                Why Sailors Trust{' '}
                <Box component="span" sx={{ display: { xs: 'block', sm: 'inline' } }}>
                    Point Sails
                </Box>
            </SectionTitle>
            
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, 1fr)',
                    lg: 'repeat(4, 1fr)'
                },
                gap: 3,
            }}>
                {trustPillars.map((pillar, index) => {
                    const IconComponent = pillar.icon;
                    return (
                        <StyledPaper key={index}>
                            <IconWrapper>
                                <IconComponent />
                            </IconWrapper>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                                    fontWeight: 600,
                                    mb: 1.5,
                                    lineHeight: 1.3,
                                }}
                            >
                                {pillar.title}
                            </Typography>
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    fontSize: '1rem',
                                    lineHeight: 1.6,
                                    color: 'text.secondary'
                                }}
                            >
                                {pillar.description}
                            </Typography>
                        </StyledPaper>
                    );
                })}
            </Box>
        </Box>
    );
};

export default TrustSection;
